import type { FunctionComponent } from "react";
import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { generatePath, useNavigate } from "react-router-dom";

import BreadcrumbsSection from "~/components/Breadcrumbs";
import ButtonNewItem from "~/components/buttons/ButtonNewItem";
import ContentWrapper from "~/components/ContentWrapper";
import SearchInput from "~/components/formElements/SearchInput";
import PageHeading from "~/components/headings/PageHeading";
import SectionHeading from "~/components/headings/SectionHeading";
import SectionHeadingTabs from "~/components/headings/SectionHeadingTabbar";
import NoEntriesFound from "~/components/NoEntriesFound";
import { appRoutes, CENTRAL_INVOICE_RECIPIENTS_INDEX_PATH } from "~/constants/appRoutes.ts";
import type { Client } from "~/modules/client/api/client/clientTypes.ts";
import type { ClientContactPersonType } from "~/modules/client/api/clientContactPerson/clientContactPersonTypes.ts";
import type { Company } from "~/modules/client/api/company/companyTypes.ts";
import CompaniesListHead from "~/modules/client/components/CompaniesListHead/CompaniesListHead.tsx";
import ContactCard from "~/modules/client/components/contact/components/ContactCard";
import CreateClientContactPersonSidebar
	from "~/modules/client/components/contact/components/CreateClientContactPersonSidebar";
import type { AvatarImage } from "~/modules/image/api/avatarImage/avatarImageTypes.ts";
import { isMobileOrTablet } from "~/utils/mobileAndTabletDetection.ts";
import { byObjectProperty } from "~/utils/sortFunctions.ts";

interface Props {
	allCompanyAvatarImages?: AvatarImage[];
	companies?: Company[];
	clients?: Client[];
	contacts?: ClientContactPersonType[];
}

const ContactsIndexView: FunctionComponent<Props> = ({ allCompanyAvatarImages,companies, clients, contacts }) => {
	const { t } = useTranslation();
	const navigate = useNavigate();

	const [newContactCompanyId, setNewContactCompanyId] = useState<string | null>(null);
	const [search, setSearch] = useState("");

	const searchInputRef = useRef<HTMLInputElement | null>(null);

	useEffect(() => {
		if (searchInputRef.current && !isMobileOrTablet()) {
			searchInputRef.current.focus();
		}
	}, []);

	const filteredContacts = useMemo(() => {
		if (contacts) {
			return contacts.filter((contact) => {
				return contact.fullName.toLowerCase().includes(search.trim().toLowerCase());
			});
		}
		return [];
	}, [search, contacts]);

	return (
		<>
			<BreadcrumbsSection pages={[appRoutes.contacts()]}
								className="bg-white" />
			<PageHeading title={t("contacts.title", "Alle Kontakte")} />

			<div className="sticky top-0 z-10 border-b bg-white py-4">
				<ContentWrapper>
					<div className="w-48">
						<SearchInput
							placeholder="Name..."
							value={search}
							onChange={setSearch}
							ref={searchInputRef} />
					</div>
				</ContentWrapper>
			</div>

			<ContentWrapper>
				<SectionHeading sticky
								style={{ top: 73 }}>
					<div className="flex items-center justify-between">
						<SectionHeadingTabs
							tabs={[
								{ name: "Kunden", value: "clients" },
								{ name: "Kontakte", value: "contacts", active: true },
								{ name: "Zentrale Rechnungsempfänger", value: "central-invoice-recipients"},
							]}
							onTabClick={(tab) => {
								"clients" === tab && navigate(appRoutes.clients().path, { replace: true });
								"contacts" === tab && navigate(appRoutes.contacts().path, { replace: true });
								"central-invoice-recipients" === tab && navigate(CENTRAL_INVOICE_RECIPIENTS_INDEX_PATH, { replace: true });
							}}
						/>
					</div>
				</SectionHeading>

				<div className="mt-8 flex flex-col gap-10">
					{0 === filteredContacts.length ? (
						<NoEntriesFound />
					) : (
						companies &&
						clients &&
						companies
							.sort(byObjectProperty("displayName"))
							.map(({ id: companyId, displayName: companyDisplayName, avatarImageId }) => {
								const companyClients = clients.filter((clients) => clients.companyId === companyId);
								const clientIds = companyClients.map((client) => client.id);
								const companiesContactPersons = filteredContacts.filter((clientContactPerson) =>
									clientIds.includes(clientContactPerson.clientId),
								);
								if (companiesContactPersons.length === 0) return null;
								const avatarImage = allCompanyAvatarImages?.find((image) => image.id === avatarImageId);
								return (
									<div key={companyId}>
										<CompaniesListHead avatarImage={avatarImage} companyName={companyDisplayName} />

										<div className="my-4 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
											{companiesContactPersons
												.sort((a, b) => a.lastName.localeCompare(b.lastName))
												.map(
													({
														id,
														clientId,
														firstName,
														lastName,
														jobTitle,
														title,
														phone,
														emailBusiness,
													}) => {
														const clientDetails = clients.find(
															(client) => clientId === client.id,
														);
														const clientDisplayName =
															undefined === clientDetails
																? ""
																: clientDetails.displayName;
														return (
															<ContactCard
																key={`client-${id}`}
																clientDisplayName={clientDisplayName}
																firstName={firstName}
																lastName={lastName}
																title={title}
																description={jobTitle}
																phone={phone}
																emailBusiness={emailBusiness}
																href={generatePath(appRoutes.contact().path, { contactId: id })}
															/>
														);
													},
												)}
											{0 === search.length && (
												<ButtonNewItem
													size="sm"
													onClick={() => setNewContactCompanyId(companyId)}
												/>
											)}
										</div>
									</div>
								);
							})
					)}
				</div>
			</ContentWrapper>
			{newContactCompanyId && (
				<CreateClientContactPersonSidebar
					isOpen={true}
					setOpen={() => setNewContactCompanyId(null)}
					companyId={newContactCompanyId}
				/>
			)}
		</>
	);
};

export default ContactsIndexView;
